import * as React from 'react'
import { Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

/** Shown when a rejected onConfirm carries no message of its own. */
const FALLBACK_ERROR = 'That didn’t work. Try again.'

/**
 * @param {unknown} error
 * @returns {string}
 */
function errorMessage(error) {
  if (typeof error === 'string' && error) return error
  return error?.apiError?.message || error?.message || FALLBACK_ERROR
}

/**
 * The one confirmation dialog: a title, a message, Cancel and one confirm
 * action. Confirming runs `onConfirm` with the dialog held open and busy
 * ("Deleting…", both buttons disabled) until it settles, then closes it on
 * success. A rejected onConfirm keeps it open and shows the error in a
 * toast. A caller that closes the dialog itself on success (and so keeps it
 * open on a handled failure) passes `closeOnConfirm={false}`, and `pending`
 * if it tracks its own busy state.
 *
 * `confirmText` asks the person to type a name first (a permanent delete of
 * something with data under it); the confirm action stays disabled until it
 * matches.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {(open: boolean) => void} props.onOpenChange
 * @param {React.ReactNode} props.title
 * @param {React.ReactNode} [props.message] - the description
 * @param {React.ReactNode} [props.children] - extra body under the message
 * @param {string} props.confirmLabel
 * @param {string} [props.pendingLabel] - shown while confirming; defaults to confirmLabel
 * @param {string} [props.cancelLabel='Cancel']
 * @param {boolean} [props.destructive=false]
 * @param {boolean} [props.pending] - the caller's own busy state
 * @param {boolean} [props.confirmDisabled=false]
 * @param {string} [props.confirmText] - text to type before confirming
 * @param {boolean} [props.closeOnConfirm=true]
 * @param {() => (void|Promise<unknown>)} props.onConfirm
 * @param {() => void} [props.onCancel]
 * @param {Object} [props.contentProps] - extra props on the dialog content
 * @returns {JSX.Element}
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  message,
  children,
  confirmLabel,
  pendingLabel,
  cancelLabel = 'Cancel',
  destructive = false,
  pending = false,
  confirmDisabled = false,
  confirmText,
  closeOnConfirm = true,
  onConfirm,
  onCancel,
  contentProps,
}) {
  const [isRunning, setIsRunning] = React.useState(false)
  const [typed, setTyped] = React.useState('')
  const isMounted = React.useRef(true)
  const inputId = React.useId()

  React.useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  React.useEffect(() => {
    if (!open) setTyped('')
  }, [open])

  const isPending = pending || isRunning
  const isTypedMismatch = Boolean(confirmText) && typed !== confirmText
  const isConfirmBlocked = isPending || confirmDisabled || isTypedMismatch

  const handleOpenChange = (next) => {
    if (!next && isPending) return
    onOpenChange?.(next)
  }

  const handleConfirm = async (event) => {
    event.preventDefault()
    if (isConfirmBlocked) return
    const result = onConfirm?.()
    // Only an async onConfirm has something to wait for.
    if (result && typeof result.then === 'function') {
      setIsRunning(true)
      let isSuccess = false
      try {
        await result
        isSuccess = true
      } catch (error) {
        // A rejected onConfirm keeps the dialog open to retry or cancel.
        toast.error(errorMessage(error))
      } finally {
        if (isMounted.current) setIsRunning(false)
      }
      if (isSuccess && closeOnConfirm) onOpenChange?.(false)
      return
    }
    if (closeOnConfirm) onOpenChange?.(false)
  }

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent aria-busy={isPending} {...contentProps}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {message ? <AlertDialogDescription>{message}</AlertDialogDescription> : null}
        </AlertDialogHeader>
        {children}
        {confirmText ? (
          <div className="space-y-2">
            <Label htmlFor={inputId}>
              Type <strong className="text-ink">{confirmText}</strong> to confirm
            </Label>
            <Input
              id={inputId}
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              placeholder={confirmText}
              autoComplete="off"
              disabled={isPending}
            />
          </div>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending} onClick={onCancel}>
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            variant={destructive ? 'destructive' : 'primary'}
            disabled={isConfirmBlocked}
            aria-busy={isPending ? 'true' : undefined}
            onClick={handleConfirm}
          >
            {isPending ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
            {isPending && pendingLabel ? pendingLabel : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export default ConfirmDialog
